// LockMenu — lock controls (Lock Now / Change Password / Remove Password).
// Used in the header action bar and on the Settings page. All lock.* calls
// are guarded because `lock` is null when the user has skipped.

import { useState, useRef, useEffect, type FormEvent } from 'react';
import type { AppLockInstance } from '@ascend/applock';
import { useAppLock } from './AppLockContext';

interface LockMenuProps {
  /** When rendered inline (Settings) instead of as a dropdown. */
  variant?: 'dropdown' | 'inline';
}

export function LockMenu({ variant = 'dropdown' }: LockMenuProps) {
  const { lock, isEnabled, enableEncryption } = useAppLock();
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<'idle' | 'change' | 'remove'>('idle');
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside.
  useEffect(() => {
    if (variant !== 'dropdown') return;
    function handler(e: MouseEvent) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setMode('idle');
      }
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [variant]);

  // ── Skipped state: offer an Enable button only. ──
  if (!isEnabled) {
    return (
      <button
        type="button"
        className="btn btn-secondary applock-enable"
        onClick={enableEncryption}
        title="Enable password protection"
      >
        Enable App Lock
      </button>
    );
  }

  const reset = () => {
    setMode('idle');
    setCurrent('');
    setNext('');
    setConfirm('');
    setError(null);
    setMessage(null);
  };

  const handleLockNow = () => {
    lock?.lock('manual');
    setOpen(false);
  };

  const handleChangePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!lock) return;
    setError(null);
    if (next.length < 6) {
      setError('New password must be at least 6 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setBusy(true);
    try {
      await lock.changePassword(current, next);
      setMessage('Password changed.');
      reset();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const handleRemovePassword = async (e: FormEvent) => {
    e.preventDefault();
    if (!lock) return;
    setError(null);
    setBusy(true);
    try {
      await lock.removePassword(current);
      setMessage('Password removed.');
      reset();
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  const lockControls = (
    <>
      {mode === 'idle' && (
        <div className="applock-menu-actions">
          <button type="button" className="btn btn-secondary" onClick={handleLockNow}>
            Lock now
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => setMode('change')}>
            Change password
          </button>
          <button type="button" className="btn btn-danger" onClick={() => setMode('remove')}>
            Remove password
          </button>
        </div>
      )}

      {mode === 'change' && (
        <form className="applock-form" onSubmit={handleChangePassword}>
          <div className="form-group">
            <label htmlFor="lock-current">Current password</label>
            <input
              id="lock-current"
              type="password"
              className="input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lock-new">New password</label>
            <input
              id="lock-new"
              type="password"
              className="input"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="lock-confirm">Confirm new password</label>
            <input
              id="lock-confirm"
              type="password"
              className="input"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              disabled={busy}
              required
            />
          </div>
          {error && (
            <p className="applock-error" role="alert">
              {error}
            </p>
          )}
          {message && (
            <p className="applock-success" role="status">
              {message}
            </p>
          )}
          <div className="applock-actions">
            <button type="submit" className="btn btn-primary" disabled={busy}>
              {busy ? 'Saving…' : 'Save new password'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {mode === 'remove' && (
        <form className="applock-form" onSubmit={handleRemovePassword}>
          <div className="form-group">
            <label htmlFor="lock-remove-current">Current password</label>
            <input
              id="lock-remove-current"
              type="password"
              className="input"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              disabled={busy}
              required
            />
          </div>
          <p className="applock-warning">
            Removing the password disables the lock screen entirely.
          </p>
          {error && (
            <p className="applock-error" role="alert">
              {error}
            </p>
          )}
          <div className="applock-actions">
            <button type="submit" className="btn btn-danger" disabled={busy}>
              {busy ? 'Removing…' : 'Remove password'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={reset} disabled={busy}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </>
  );

  if (variant === 'inline') {
    return (
      <div className="applock-inline">
        <h4>App Lock</h4>
        {lockControls}
      </div>
    );
  }

  // Dropdown variant (header)
  return (
    <div className="applock-dropdown" ref={wrapperRef}>
      <button
        type="button"
        className="btn btn-secondary applock-trigger"
        onClick={() => {
          setOpen((v) => !v);
          reset();
        }}
        aria-expanded={open}
        aria-label="App lock menu"
      >
        Lock
      </button>
      {open && (
        <div className="applock-dropdown-panel" role="menu">
          {lockControls}
        </div>
      )}
    </div>
  );
}

export type { LockMenuProps, AppLockInstance };
